// src/products/userInvites/pages/SendInvitePage.js
// Purpose: Main page for sending new user invitations with product/context selection
// Note: This is a page component - header/sidebar handled by UserInvitesApp

import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../../config';
import { userInvitesApi } from '../api/userInvitesApi';
import { 
  Mail, 
  Send, 
  UserPlus, 
  AlertCircle, 
  CheckCircle, 
  Building, 
  Users,
  Database,
  HelpCircle,
  Users as UsersIcon
} from 'lucide-react';
import BulkInviteModal from '../components/BulkInviteModal';

const SendInvitePage = ({ user }) => {
  const [formData, setFormData] = useState({
    email: '',
    products: ['dataroom'], // Changed to array for multiple products
    role: 'viewer',
    projectId: '',
    roundId: '',
    customMessage: ''
  });

  const [availableProducts] = useState([
    { id: 'dataroom', name: 'Dataroom', icon: Database, description: 'Document sharing and collaboration' },
    { id: 'capraise', name: 'CapRaise', icon: Building, description: 'Capital raising and investor management' },
    { id: 'equity', name: 'Equity', icon: Users, description: 'Cap table and equity tracking' },
    { id: 'questions', name: 'Questions', icon: HelpCircle, description: 'Q&A and discussion threads' }
  ]);

  // Users can only invite Viewers - Creator role requires a paid subscription
  const [roles] = useState([
    { id: 'viewer', name: 'Viewer', description: 'Can view content but not edit' }
    // Support level is not yet functional - hidden from end users
    // { id: 'support', name: 'Support', description: 'Can view, upload, and organize content (cannot create/delete projects or see billing)' },
    // Creator role cannot be assigned via invite - users must subscribe to become Creators
    // { id: 'creator', name: 'Creator', description: 'Full access including user management and project creation' }
  ]);

  const [projects, setProjects] = useState([]);
  const [rounds, setRounds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [showBulkModal, setShowBulkModal] = useState(false);

  // Load projects/rounds when product changes
  const loadProjects = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('dataroom_projects')
        .select(`
          id, 
          project_name, 
          description,
          dataroom_users!inner(user_id)
        `)
        .eq('dataroom_users.user_id', user.id)
        .order('project_name');

      if (error) throw error;
      setProjects(data || []);
    } catch (error) {
      console.error('Error loading projects:', error);
      setProjects([]);
    }
  }, [user.id]);

  const loadRounds = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('capraise_rounds')
        .select('id, round_name, round_type, status')
        .eq('user_id', user.id)
        .order('round_name');

      if (error) throw error;
      setRounds(data || []);
    } catch (error) {
      console.error('Error loading rounds:', error);
      setRounds([]);
    }
  }, [user.id]);

  useEffect(() => {
    // Load projects/rounds when any selected product changes
    if (formData.products.includes('dataroom')) {
      loadProjects();
    }
    if (formData.products.includes('capraise')) {
      loadRounds();
    }
  }, [formData.products, loadProjects, loadRounds]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear context when products change
    if (field === 'products') {
      setFormData(prev => ({
        ...prev,
        projectId: '',
        roundId: ''
      }));
    }
  };

  const toggleProduct = (productId) => {
    const updatedProducts = formData.products.includes(productId)
      ? formData.products.filter(p => p !== productId)
      : [...formData.products, productId];
    
    handleInputChange('products', updatedProducts);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.email || formData.products.length === 0) {
      setMessage({ type: 'error', text: 'Please fill in all required fields.' });
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setMessage({ type: 'error', text: 'Please enter a valid email address.' });
      return;
    }

    setLoading(true);
    setMessage(null);

    try {
      const results = {
        success: 0,
        failed: 0,
        errors: []
      };

      // Send separate invite for each selected product
      for (const productId of formData.products) {
        const inviteData = {
          email: formData.email,
          inviteforproduct: productId,
          role: formData.role,
          invitedby: user.id,
          custom_message: formData.customMessage
        };

        // Add context based on product
        if (productId === 'dataroom' && formData.projectId) {
          inviteData.projectid = parseInt(formData.projectId);
        }
        if (productId === 'capraise' && formData.roundId) {
          inviteData.roundid = parseInt(formData.roundId);
        }

        try {
          const response = await userInvitesApi.createInvite(inviteData);
          
          if (response.status === 'success') {
            results.success++;
          } else {
            results.failed++;
            results.errors.push(`${productId}: ${response.message}`);
          }
        } catch (error) {
          results.failed++;
          results.errors.push(`${productId}: ${error.message}`);
        }
      }

      if (results.failed === 0) {
        setMessage({
          type: 'success',
          text: `Successfully sent ${results.success} invitation${results.success > 1 ? 's' : ''} to ${formData.email}!`
        });
        
        // Reset form
        setFormData({
          email: '',
          products: ['dataroom'],
          role: 'viewer',
          projectId: '',
          roundId: '',
          customMessage: ''
        });
      } else {
        setMessage({
          type: 'warning',
          text: `Sent ${results.success} invitation${results.success !== 1 ? 's' : ''}, ${results.failed} failed. Check console for details.`
        });
        console.error('Invite errors:', results.errors);
      }

    } catch (error) {
      console.error('Error sending invitation:', error);
      setMessage({
        type: 'error',
        text: error.message || 'Failed to send invitation. Please try again.'
      });
    } finally {
      setLoading(false);
    }
  };

  const getSelectedProducts = () => {
    return availableProducts.filter(p => formData.products.includes(p.id));
  };

  const getSelectedRole = () => {
    return roles.find(r => r.id === formData.role);
  };

  return (
    <div className="p-6">
      {/* Page Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center">
            <UserPlus className="w-8 h-8 text-blue-600 mr-3" />
            <h1 className="text-3xl font-bold text-gray-900">Send Invitation</h1>
          </div>
          <button
            onClick={() => setShowBulkModal(true)}
            className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
          >
            <UsersIcon className="w-4 h-4 mr-2" />
            Bulk Invite
          </button>
        </div>
        <p className="text-gray-600">
          Invite users to access specific products and projects. They'll receive a magic link to get started.
        </p>
      </div>

      {/* Message Display */}
      {message && (
        <div className={`mb-6 p-4 rounded-lg flex items-center ${
          message.type === 'success' 
            ? 'bg-green-50 border border-green-200 text-green-800'
            : 'bg-red-50 border border-red-200 text-red-800'
        }`}>
          {message.type === 'success' 
            ? <CheckCircle className="w-5 h-5 mr-2" />
            : <AlertCircle className="w-5 h-5 mr-2" />
          }
          {message.text}
        </div>
      )}

      {/* Invitation Form */}
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          
          {/* Email Input */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Email Address *
            </label>
            <div className="relative">
              <Mail className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
              <input
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="user@example.com"
                required
              />
            </div>
          </div>

          {/* Product Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Product Access * (Select one or more)
            </label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {availableProducts.map((product) => {
                const Icon = product.icon;
                const isSelected = formData.products.includes(product.id);
                
                return (
                  <div
                    key={product.id}
                    onClick={() => toggleProduct(product.id)}
                    className={`p-4 border rounded-lg cursor-pointer transition-all ${
                      isSelected
                        ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-200'
                        : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-start">
                      <Icon className={`w-6 h-6 mr-3 mt-1 ${isSelected ? 'text-blue-600' : 'text-gray-400'}`} />
                      <div className="flex-1">
                        <h3 className={`font-medium ${isSelected ? 'text-blue-900' : 'text-gray-900'}`}>
                          {product.name}
                        </h3>
                        <p className={`text-sm ${isSelected ? 'text-blue-700' : 'text-gray-600'}`}>
                          {product.description}
                        </p>
                      </div>
                      {isSelected && (
                        <div className="flex-shrink-0">
                          <div className="w-5 h-5 bg-blue-600 rounded-full flex items-center justify-center">
                            <CheckCircle className="w-3 h-3 text-white" />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            <p className="text-sm text-gray-500 mt-2">
              Selected: {getSelectedProducts().map(p => p.name).join(', ') || 'None'}
            </p>
          </div>

          {/* Context Selection - Dataroom Projects */}
          {formData.products.includes('dataroom') && projects.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Specific Dataroom Project (Optional)
              </label>
              <select
                value={formData.projectId}
                onChange={(e) => handleInputChange('projectId', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">All dataroom projects</option>
                {projects.map(project => (
                  <option key={project.id} value={project.id}>
                    {project.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Context Selection - CapRaise Rounds */}
          {formData.products.includes('capraise') && rounds.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Specific CapRaise Round (Optional)
              </label>
              <select
                value={formData.roundId}
                onChange={(e) => handleInputChange('roundId', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">All capraise rounds</option>
                {rounds.map(round => (
                  <option key={round.id} value={round.id}>
                    {round.round_name} ({round.round_type})
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Role Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Role & Permissions *
            </label>
            <div className="space-y-3">
              {roles.map((role) => {
                const isSelected = formData.role === role.id;
                
                return (
                  <div
                    key={role.id}
                    onClick={() => handleInputChange('role', role.id)}
                    className={`p-3 border rounded-lg cursor-pointer transition-all ${
                      isSelected
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-center">
                      <input
                        type="radio"
                        name="role"
                        value={role.id}
                        checked={isSelected}
                        onChange={() => handleInputChange('role', role.id)}
                        className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                      />
                      <div className="ml-3">
                        <h4 className={`font-medium ${isSelected ? 'text-blue-900' : 'text-gray-900'}`}>
                          {role.name}
                        </h4>
                        <p className={`text-sm ${isSelected ? 'text-blue-700' : 'text-gray-600'}`}>
                          {role.description}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-800">
                <strong>Note:</strong> Invited users will be Viewers. To become a Creator with full access (create dealrooms, manage equity, etc.), users must subscribe separately.
              </p>
            </div>
          </div>

          {/* Custom Message */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Custom Message (Optional)
            </label>
            <textarea
              value={formData.customMessage}
              onChange={(e) => handleInputChange('customMessage', e.target.value)}
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Add a personal message to include with the invitation..."
            />
            <p className="text-sm text-gray-500 mt-1">
              This message will be included in the invitation email.
            </p>
          </div>

          {/* Preview Section */}
          <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
            <h3 className="font-medium text-gray-900 mb-3">Invitation Preview</h3>
            <div className="text-sm text-gray-700 space-y-2">
              <p><strong>To:</strong> {formData.email || 'user@example.com'}</p>
              <p><strong>Products:</strong> {getSelectedProducts().map(p => p.name).join(', ') || 'None'}</p>
              <p><strong>Role:</strong> {getSelectedRole()?.name}</p>
              {formData.products.includes('dataroom') && formData.projectId && (
                <p><strong>Dataroom Project:</strong> {projects.find(p => p.id === parseInt(formData.projectId))?.name}</p>
              )}
              {formData.products.includes('capraise') && formData.roundId && (
                <p><strong>CapRaise Round:</strong> {rounds.find(r => r.id === parseInt(formData.roundId))?.round_name}</p>
              )}
              {formData.customMessage && (
                <div>
                  <strong>Custom Message:</strong>
                  <div className="mt-1 p-2 bg-white rounded border italic">
                    {formData.customMessage}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex justify-end pt-4">
            <button
              type="submit"
              disabled={loading || !formData.email || formData.products.length === 0}
              className={`flex items-center px-6 py-3 rounded-lg font-medium transition-all ${
                loading || !formData.email || formData.products.length === 0
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-blue-600 text-white hover:bg-blue-700 focus:ring-2 focus:ring-blue-500'
              }`}
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Sending...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Send Invitation
                </>
              )}
            </button>
          </div>

        </form>
      </div>

      {/* Help Section */}
      <div className="mt-8 bg-blue-50 rounded-lg p-6 border border-blue-200">
        <h3 className="font-medium text-blue-900 mb-3 flex items-center">
          <HelpCircle className="w-5 h-5 mr-2" />
          How Invitations Work
        </h3>
        <div className="text-sm text-blue-800 space-y-2">
          <p>• Invited users receive a magic link via email for instant access</p>
          <p>• If magic links fail, users can create accounts manually with the same email</p>
          <p>• Users are automatically granted the specified role and product access</p>
          <p>• You can track invitation status in the "Manage Invites" section</p>
        </div>
      </div>

      {/* Bulk Invite Modal */}
      <BulkInviteModal
        isOpen={showBulkModal}
        onClose={() => setShowBulkModal(false)}
        user={user}
        onSuccess={() => {
          setMessage({
            type: 'success',
            text: 'Bulk invites completed! Check the Manage Invites section to see the results.'
          });
        }}
      />
    </div>
  );
};

export default SendInvitePage;